// src/shared/ui/DatePicker.jsx
//
// Shared date field: a formatted trigger plus a react-day-picker calendar.
//
// This replaced a native <input type="date">. Native inputs render using the
// OS locale and give no control over the placeholder, so on a machine whose
// short-date pattern is `dd-MMM-yy` Chrome showed an empty field as
// "dd-----yyyy" -- it has no 3-letter hint for a text month and falls back to
// dashes. Every user saw a different format depending on their OS, and none
// of it was ours to fix.
//
// Public API is unchanged from the native version, so the 11 call sites did
// not move: `value` is ISO `YYYY-MM-DD`, `onChange` receives ISO (or "" when
// cleared), and `min`/`max` couple two fields into a range.
//
// Dates are parsed and formatted with date-fns, never Date.toISOString():
// parseISO("2026-09-01") is local midnight, and toISOString() would shift it
// a day backwards for anyone behind UTC.

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { DayPicker } from "react-day-picker";
import { format, isValid, parseISO } from "date-fns";
import { Calendar, X } from "lucide-react";
import { isIsoDate } from "../utils/dateHelpers";
import "react-day-picker/style.css";
import "./formControls.css";

const DISPLAY_FORMAT = "dd-MMM-yyyy";
const ISO_FORMAT = "yyyy-MM-dd";

function toDate(iso) {
  if (!isIsoDate(iso)) return undefined;
  const parsed = parseISO(iso);
  return isValid(parsed) ? parsed : undefined;
}

export default function DatePicker({
  value,
  onChange,
  min,
  max,
  className = "",
  invalid,
  bare = false,
  disabled = false,
  placeholder = "DD-MMM-YYYY",
  id,
  ...props
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const wrapperRef = useRef(null);
  const popoverRef = useRef(null);
  const generatedId = useId();
  const dialogId = `${id || generatedId}-calendar`;

  const selected = toDate(value);
  const minDate = toDate(min);
  const maxDate = toDate(max);

  // Out of range is still reported rather than assumed impossible: `value`
  // can arrive from persisted state or an API that never saw these bounds.
  const outOfRange =
    Boolean(selected) &&
    ((minDate && selected < minDate) || (maxDate && selected > maxDate));
  const isInvalid = Boolean(invalid) || outOfRange;

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const inTrigger = wrapperRef.current?.contains(event.target);
      const inPopover = popoverRef.current?.contains(event.target);
      if (!inTrigger && !inPopover) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const place = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POPOVER_H = 340;  // calendar + dropdowns, measured
    const POPOVER_W = 300;
    const below = window.innerHeight - r.bottom;
    const flipUp = below < POPOVER_H && r.top > below;
    setCoords({
      top: flipUp ? Math.max(8, r.top - POPOVER_H - 4) : r.bottom + 4,
      // Keep it on screen when the trigger sits near the right edge.
      left: Math.min(Math.max(8, r.left), window.innerWidth - POPOVER_W - 8),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    // capture:true so scrolling any ancestor (the tables here are
    // overflow-x-auto) keeps the calendar glued to its trigger.
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  const disabledMatcher = useMemo(() => {
    const rules = [];
    if (minDate) rules.push({ before: minDate });
    if (maxDate) rules.push({ after: maxDate });
    return rules.length ? rules : undefined;
  }, [minDate, maxDate]);

  // The year dropdown needs an explicit span: left to itself it covers a
  // narrow range around today, which would not reach the end of a project
  // starting next year. min/max still win where a caller set them.
  const { navStart, navEnd } = useMemo(() => {
    const anchor = selected || minDate || maxDate || new Date();
    const year = anchor.getFullYear();
    return {
      navStart: minDate || new Date(year - 5, 0, 1),
      navEnd: maxDate || new Date(year + 5, 11, 31),
    };
  }, [selected, minDate, maxDate]);

  const handleSelect = (date) => {
    onChange?.(date ? format(date, ISO_FORMAT) : "");
    close();
  };

  return (
    <div className="shui-datefield" ref={wrapperRef}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        aria-invalid={isInvalid || undefined}
        data-invalid={isInvalid ? "true" : undefined}
        onClick={() => setOpen((v) => !v)}
        className={`${bare ? "" : "shui-input "}shui-datefield__trigger ${className}`.trim()}
        {...props}
      >
        <span className={selected ? "" : "shui-datefield__placeholder"}>
          {selected ? format(selected, DISPLAY_FORMAT) : placeholder}
        </span>
        <span className="shui-datefield__icons">
          {selected && !disabled && (
            // A nested <button> is invalid inside a <button>, so clearing is
            // a span with button semantics rather than a real element.
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear date"
              className="shui-datefield__clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange?.("");
                }
              }}
            >
              <X size={13} />
            </span>
          )}
          <Calendar size={14} aria-hidden="true" />
        </span>
      </button>

      {/* Portalled to <body>: rendered in place, any ancestor with
          overflow (the milestone/resource tables are overflow-x-auto)
          clips the calendar and forces a scrollbar inside the section
          instead of letting it float above the page. */}
      {open && coords && createPortal(
        <div
          ref={popoverRef}
          className="shui-datefield__popover"
          role="dialog"
          aria-modal="false"
          id={dialogId}
          style={{ top: coords.top, left: coords.left }}
        >
          <DayPicker
            mode="single"
            autoFocus
            selected={selected}
            defaultMonth={selected || minDate}
            onSelect={handleSelect}
            disabled={disabledMatcher}
            startMonth={navStart}
            endMonth={navEnd}
            captionLayout="dropdown"
          />
        </div>,
        document.body
      )}
    </div>
  );
}

DatePicker.propTypes = {
  /** ISO `YYYY-MM-DD`. Anything else renders as the placeholder. */
  value: PropTypes.string,
  /** Receives the ISO string, or "" when cleared. */
  onChange: PropTypes.func,
  min: PropTypes.string,
  max: PropTypes.string,
  className: PropTypes.string,
  /** Force the invalid treatment for a reason this component can't see. */
  invalid: PropTypes.bool,
  /** Drop the .shui-input box, for compact table cells with their own sizing. */
  bare: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  id: PropTypes.string,
};
